/**
 * Финансовая разметка: то, чего нет в 1С и что решает клиент.
 *
 * Три справочника и одна настройка:
 *   — свои организации среди контрагентов (по ИНН и вручную);
 *   — статьи ДДС: раздел, направление, строка P&L;
 *   — номенклатура услуг в поступлениях: строка P&L;
 *   — входят ли долги между своими в консолидированный итог.
 *
 * Начальное заполнение делают правила по имени, правка человека сильнее
 * правил и помечается «вручную». Отчёты читают разметку сразу, без пересчёта.
 */

import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  ApiError,
  financeApi,
  type CashflowItemClassRow,
  type DebtBranchCoverageRow,
  type ExpenseItemClassRow,
  type FinanceOptions,
  type FinanceSettings,
  type OwnCounterpartyRow,
  exclusionsApi,
  type ExcludedDocumentRow,
  type FoundDocumentRow,
} from '../api'
import { Badge, Card, Empty, Loading, Notice, TableWrap, Tabs } from '../components/ui'
import { Icon } from '../components/Icon'
import { Segmented } from '../finance/parts'
import { durationToken } from '../finance/motion'
import { formatDate, formatMoney, formatMoneyShort, formatMonth, formatPercent, formatRub, pluralize, toNumber } from '../format'
import { forgetFinanceSettings } from '../finance/settings'

type Tab = 'own' | 'cashflow' | 'expense' | 'excluded'

const TABS: Array<{ code: Tab; label: string }> = [
  { code: 'own', label: 'Свои организации' },
  { code: 'cashflow', label: 'Статьи ДДС' },
  { code: 'expense', label: 'Статьи расходов' },
  { code: 'excluded', label: 'Исключённые документы' },
]

export function AdminFinance() {
  const [params, setParams] = useSearchParams()
  const tab = (TABS.find((item) => item.code === params.get('tab'))?.code ?? 'own') as Tab
  const [options, setOptions] = useState<FinanceOptions | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    financeApi
      .options()
      .then(setOptions)
      .catch((caught) => setError(caught instanceof ApiError ? caught.message : 'Не удалось загрузить справочник значений'))
  }, [])

  return (
    <div className="stack" style={{ gap: 18 }}>
      <Notice tone="info">
        В 1С нет ни раздела у статей ДДС, ни статьи расходов в поступлениях, ни признака «своя организация» у
        контрагента. Разметка ниже заполнена правилами по наименованию; поправьте то, что не так, — отчёты
        увидят правку сразу.
      </Notice>
      {error && <Notice tone="bad">{error}</Notice>}

      <Tabs
        label="Справочники разметки"
        value={tab}
        onChange={(code) => setParams(code === 'own' ? {} : { tab: code })}
        items={TABS.map((item) => ({ value: item.code, label: item.label }))}
      />

      <div className="tab-panel" key={tab}>
      {!options && !error ? (
        <Loading rows={6} />
      ) : options ? (
        tab === 'own' ? (
          <OwnTab />
        ) : tab === 'cashflow' ? (
          <CashflowTab options={options} />
        ) : tab === 'expense' ? (
          <ExpenseTab options={options} />
        ) : (
          <ExcludedTab />
        )
      ) : null}
      </div>
    </div>
  )
}

/**
 * Действие с защитой от двойного клика и текстом ошибки сервера.
 *
 * Блокируется только тот контрол, что сохраняется (ключ — строка),
 * остальная таблица работает. После ответа рядом с ним на полторы
 * секунды встаёт «Сохранено», строка коротко подсвечивается.
 */
function useAction(reload: () => Promise<void>) {
  const [busy, setBusy] = useState<Set<string>>(() => new Set())
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState<{ key: string; leaving: boolean; id: number } | null>(null)
  const timers = useRef<number[]>([])
  useEffect(() => () => timers.current.forEach((id) => window.clearTimeout(id)), [])

  const run = async (key: string, action: () => Promise<unknown>) => {
    if (busy.has(key)) return false
    setBusy((current) => new Set(current).add(key))
    setError(null)
    try {
      await action()
      await reload()
      const id = Date.now()
      setSaved({ key, leaving: false, id })
      timers.current.push(
        window.setTimeout(() => setSaved((current) => (current?.id === id ? { ...current, leaving: true } : current)), SAVED_SHOWN),
        window.setTimeout(() => setSaved((current) => (current?.id === id ? null : current)), SAVED_SHOWN + durationToken('--dur-leave', 200)),
      )
      return true
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Не удалось сохранить')
      return false
    } finally {
      setBusy((current) => {
        const next = new Set(current)
        next.delete(key)
        return next
      })
    }
  }
  const isBusy = (key: string) => busy.has(key)
  /** Отметка «Сохранено» у контрола строки. */
  const mark = (key: string) => (saved?.key === key ? <SavedMark key={saved.id} leaving={saved.leaving} /> : null)
  /** Класс строки: короткая подсветка после сохранения. */
  const flash = (key: string) => (saved?.key === key && !saved.leaving ? 'row--flash' : undefined)
  return { busy: busy.size > 0, isBusy, error, run, mark, flash }
}

const SAVED_SHOWN = 1500

function SavedMark({ leaving }: { leaving: boolean }) {
  return (
    <span className={`saved-mark${leaving ? ' saved-mark--leave' : ''}`} role="status">
      <Icon name="check" size={12} /> Сохранено
    </span>
  )
}

const money = (value: unknown) => (toNumber(value) ? formatMoneyShort(value) : '—')

// --- Свои организации ---------------------------------------------------------

function OwnTab() {
  const [rows, setRows] = useState<OwnCounterpartyRow[] | null>(null)
  const [coverage, setCoverage] = useState<DebtBranchCoverageRow[]>([])
  const [settings, setSettings] = useState<FinanceSettings | null>(null)
  const [query, setQuery] = useState('')
  const [found, setFound] = useState<Array<{ guid: string; name: string; inn: string | null; is_own: boolean }> | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)

  const load = async () => {
    try {
      const [data, current] = await Promise.all([financeApi.counterparties(), financeApi.settings()])
      setRows(data.rows)
      setCoverage(data.coverage)
      setSettings(current)
    } catch (caught) {
      setLoadError(caught instanceof ApiError ? caught.message : 'Не удалось загрузить контрагентов')
    }
  }
  const { isBusy, error, run, mark, flash } = useAction(load)

  useEffect(() => {
    void load()
  }, [])

  useEffect(() => {
    const needle = query.trim()
    if (needle.length < 2) {
      setFound(null)
      return
    }
    const id = window.setTimeout(() => {
      financeApi
        .searchCounterparties(needle)
        .then((data) => setFound(data.rows))
        .catch(() => setFound([]))
    }, 250)
    return () => window.clearTimeout(id)
  }, [query])

  if (loadError) return <Notice tone="bad">{loadError}</Notice>
  if (!rows || !settings) return <Loading rows={6} />

  const own = rows.filter((row) => row.is_own)
  const ownReceivable = own.reduce((sum, row) => sum + (toNumber(row.receivable) ?? 0), 0)
  const ownPayable = own.reduce((sum, row) => sum + (toNumber(row.payable) ?? 0), 0)
  const exclude = settings.debts_intercompany === 'exclude'

  const setMode = (mode: FinanceSettings['debts_intercompany']) =>
    run('settings', async () => {
      await financeApi.setSettings({ debts_intercompany: mode })
      forgetFinanceSettings()
    })

  return (
    <div className="stack" style={{ gap: 18 }}>
      {error && <Notice tone="bad">{error}</Notice>}

      <Card
        title="Долги между своими в общем итоге"
        subtitle="Решение собственника ещё не получено — переключается здесь, без программиста"
      >
        <div className="stack" style={{ gap: 12 }}>
          <div className="row row--tight">
            <Segmented
              label="Долги между своими в общем итоге"
              value={settings.debts_intercompany}
              onChange={(mode) => {
                if (mode !== settings.debts_intercompany && !isBusy('settings')) void setMode(mode)
              }}
              options={[
                { value: 'exclude', label: 'Не включать (по умолчанию)' },
                { value: 'include', label: 'Включать' },
              ]}
            />
            {mark('settings')}
          </div>
          <p className="small muted">
            {exclude
              ? `Сейчас дебиторка, кредиторка и сигналы просрочки в финансовом блоке — только по внешним контрагентам. Долги своих организаций (дебиторка ${formatRub(ownReceivable)}, кредиторка ${formatRub(ownPayable)}) показываются отдельной строкой «не включено».`
              : `Сейчас долги своих организаций входят в общий итог: дебиторка ${formatRub(ownReceivable)}, кредиторка ${formatRub(ownPayable)}. Раздел «Долги» по-прежнему умеет показать внешних и своих по отдельности.`}
          </p>
        </div>
      </Card>

      <Card
        flush
        title="Контрагенты — свои организации"
        subtitle={`${pluralize(own.length, 'контрагент считается', 'контрагента считаются', 'контрагентов считаются')} своими. По ИНН — совпадение с организацией справочника; правка вручную сильнее ИНН.`}
      >
        {rows.length === 0 ? (
          <Empty title="Ни один контрагент не совпал по ИНН с организациями" hint="Отметьте своих вручную через поиск ниже." />
        ) : (
          <TableWrap cards>
            <table className="data">
              <thead>
                <tr>
                  <th>Контрагент</th>
                  <th>ИНН</th>
                  <th>Признак</th>
                  <th className="num">Дебиторка</th>
                  <th className="num">Кредиторка</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.guid} className={flash(row.guid)}>
                    <td>
                      <div className="strong">{row.name}</div>
                      {row.organization && <div className="tiny muted">ИНН организации «{row.organization}»</div>}
                    </td>
                    <td className="mono small">{row.inn ?? '—'}</td>
                    <td>
                      {row.is_own ? <Badge tone="accent">своя</Badge> : <Badge>внешний</Badge>}{' '}
                      <span className="tiny muted">{row.source === 'manual' ? `вручную${row.updated_by ? `, ${row.updated_by}` : ''}` : 'по ИНН'}</span>
                    </td>
                    <td className="num mono">{money(row.receivable)}</td>
                    <td className="num mono">{money(row.payable)}</td>
                    <td className="num">
                      <div className="row row--tight" style={{ justifyContent: 'flex-end' }}>
                        {mark(row.guid)}
                        <button type="button" className="btn btn--sm btn--ghost" disabled={isBusy(row.guid)} onClick={() => void run(row.guid, () => financeApi.setCounterparty(row.guid, !row.is_own))}>
                          {row.is_own ? 'Считать внешним' : 'Считать своей'}
                        </button>
                        {row.source === 'manual' && (
                          <button type="button" className="btn btn--sm btn--ghost" disabled={isBusy(row.guid)} onClick={() => void run(row.guid, () => financeApi.setCounterparty(row.guid, null))}>
                            По ИНН
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>

      <Card title="Отметить контрагента своим вручную" subtitle="Для своих организаций без ИНН или с другим ИНН. Поиск по наименованию или началу ИНН">
        <div className="stack" style={{ gap: 12 }}>
          <input className="input" type="search" placeholder="Например, «Малышкин» или 6662…" value={query} onChange={(event) => setQuery(event.target.value)} aria-label="Поиск контрагента" />
          {found !== null &&
            (found.length === 0 ? (
              <div className="small muted">Ничего не нашлось.</div>
            ) : (
              <ul className="stack" style={{ gap: 6, listStyle: 'none', padding: 0, margin: 0 }}>
                {found.map((item) => (
                  <li key={item.guid} className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
                    <span>
                      {item.name} <span className="tiny muted mono">{item.inn ?? ''}</span>
                    </span>
                    {item.is_own ? (
                      <Badge tone="accent">своя</Badge>
                    ) : (
                      <button type="button" className="btn btn--sm" disabled={isBusy(item.guid)} onClick={() => void run(item.guid, async () => {
                        await financeApi.setCounterparty(item.guid, true)
                        setQuery('')
                      })}>
                        Считать своей
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            ))}
        </div>
      </Card>

      {coverage.length > 0 && <CoverageCard rows={coverage} />}
    </div>
  )
}

/** Откуда известен город у долга — почему долги не выдаются по городам. */
function CoverageCard({ rows }: { rows: DebtBranchCoverageRow[] }) {
  const external = rows.filter((row) => row.kind === 'Дебиторская' && row.relation === 'Внешние')
  const total = external.reduce((sum, row) => sum + (toNumber(row.amount) ?? 0), 0)
  const of = (source: string) => external.filter((row) => row.branch_source === source).reduce((sum, row) => sum + (toNumber(row.amount) ?? 0), 0)
  return (
    <Card title="Город у дебиторки внешних контрагентов" subtitle="Поэтому директорам филиалов долги по городам пока не выдаются">
      {total === 0 ? (
        <div className="small muted">Дебиторки внешних контрагентов на дату снимка нет.</div>
      ) : (
        <ul className="stack small" style={{ gap: 4, margin: 0, paddingLeft: 18 }}>
          <li>по документу-основанию — {formatRub(of('По документу'))} ({formatPercent((of('По документу') / total) * 100)})</li>
          <li>выведено по последней продаже или поступлению — {formatRub(of('Выведено по контрагенту'))} ({formatPercent((of('Выведено по контрагенту') / total) * 100)})</li>
          <li>не определён — {formatRub(of('Не определён'))} ({formatPercent((of('Не определён') / total) * 100)})</li>
        </ul>
      )}
    </Card>
  )
}

// --- Исключённые документы -----------------------------------------------------

function ExcludedTab() {
  const [rows, setRows] = useState<ExcludedDocumentRow[] | null>(null)
  const [query, setQuery] = useState('')
  const [found, setFound] = useState<FoundDocumentRow[] | null>(null)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [loadError, setLoadError] = useState<string | null>(null)
  const [toast, setToast] = useState<{ text: string; id: number } | null>(null)
  // Строка, которая только что появилась (подсветка), и строка, которая уходит (гаснет до удаления).
  const [fresh, setFresh] = useState<string | null>(null)
  const [leaving, setLeaving] = useState<string | null>(null)

  const load = async () => {
    try {
      setRows((await exclusionsApi.list()).documents)
    } catch (caught) {
      setLoadError(caught instanceof ApiError ? caught.message : 'Не удалось загрузить список')
    }
  }
  const { isBusy, error, run } = useAction(load)

  useEffect(() => {
    void load()
  }, [])

  const search = async () => {
    setSearchError(null)
    try {
      setFound((await exclusionsApi.search(query)).documents)
    } catch (caught) {
      setFound(null)
      setSearchError(caught instanceof ApiError ? caught.message : 'Поиск не удался')
    }
  }

  if (loadError) return <Notice tone="bad">{loadError}</Notice>
  if (!rows) return <Loading rows={4} />

  const keyOf = (doc: { database_id: string; doc_guid: string }) => `${doc.database_id}:${doc.doc_guid}`

  return (
    <div className="stack" style={{ gap: 18 }}>
      {error && <div className="fin-appear"><Notice tone="bad">{error}</Notice></div>}

      <Card
        title="Исключить документ из отчётов"
        subtitle="Для тестовых и ошибочных документов. В 1С ничего не меняется, сырьё у нас остаётся; документ не вернётся и с переприсланным месяцем"
      >
        <form
          className="stack"
          style={{ gap: 12 }}
          onSubmit={(event) => {
            event.preventDefault()
            void search()
          }}
        >
          <div className="search-row">
            <input
              className="input"
              type="search"
              placeholder="Номер документа, например Т-Е-0005325, или GUID"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              aria-label="Номер или GUID документа"
            />
            <button type="submit" className="btn">
              Найти
            </button>
          </div>
          {searchError && <div className="small muted">{searchError}</div>}
          {found !== null &&
            (found.length === 0 ? (
              <div className="small muted fin-appear">Документ с таким номером или GUID не найден.</div>
            ) : (
              <div className="stack fin-appear" style={{ gap: 12 }}>
                {found.some((doc) => !doc.excluded) && (
                  <input
                    className="input"
                    placeholder="Причина — например, «тестовый документ подрядчика»"
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    aria-label="Причина исключения"
                  />
                )}
                <ul className="stack" style={{ gap: 6, listStyle: 'none', padding: 0, margin: 0 }}>
                  {found.map((doc) => (
                    <li key={keyOf(doc)} className="row" style={{ justifyContent: 'space-between', gap: 8 }}>
                      <span title={`База ${doc.database_id}`}>
                        {doc.stream_title} {doc.doc_number ?? ''} от {formatDate(doc.doc_date)} — {formatMoney(doc.amount)} ₽
                        <span className="tiny muted"> · {pluralize(doc.lines, 'строка', 'строки', 'строк')}</span>
                      </span>
                      {doc.excluded ? (
                        <Badge>исключён</Badge>
                      ) : (
                        <button
                          type="button"
                          className="btn btn--sm"
                          disabled={isBusy(doc.doc_guid) || reason.trim() === ''}
                          title={reason.trim() === '' ? 'Сначала напишите причину' : undefined}
                          onClick={() =>
                            void run(doc.doc_guid, async () => {
                              const result = await exclusionsApi.exclude({ database_id: doc.database_id, doc_guid: doc.doc_guid, reason })
                              // Найденный документ остаётся на месте с пометкой «исключён»:
                              // карточка не схлопывается, список ниже не прыгает.
                              setFound((current) => current?.map((item) => (keyOf(item) === keyOf(doc) ? { ...item, excluded: true } : item)) ?? null)
                              setReason('')
                              setFresh(keyOf(doc))
                              setToast({
                                text: `${doc.stream_title} ${doc.doc_number ?? ''} исключён: ${pluralize(result.lines, 'строка убрана', 'строки убраны', 'строк убрано')} из отчётов, витрины пересчитаны.`,
                                id: Date.now(),
                              })
                            })
                          }
                        >
                          {isBusy(doc.doc_guid) ? 'Исключаем…' : 'Исключить'}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
        </form>
      </Card>

      <Card flush title="Исключены сейчас" subtitle={pluralize(rows.length, 'документ', 'документа', 'документов')}>
        {rows.length === 0 ? (
          <Empty title="Исключённых документов нет" hint="Все загруженные документы входят в отчёты." />
        ) : (
          <TableWrap cards>
            <table className="data">
              <thead>
                <tr>
                  <th>Документ</th>
                  <th className="num">Сумма</th>
                  <th>Причина</th>
                  <th>Кто и когда</th>
                  <th aria-label="Действие" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const key = keyOf(row)
                  return (
                    <tr key={key} className={key === leaving ? 'row--leave' : key === fresh ? 'row--new row--flash' : undefined}>
                      <td>
                        {row.stream_title ?? 'Документ'} {row.doc_number ?? ''}
                        {row.doc_date && <span className="tiny muted"> от {formatDate(row.doc_date)}</span>}
                      </td>
                      <td className="num mono">{formatMoney(row.amount)}</td>
                      <td className="wrap-cell">{row.reason}</td>
                      <td className="small muted">
                        {row.created_by ?? '—'}, {formatDate(row.created_at)}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn--sm"
                          disabled={isBusy(`in:${row.doc_guid}`)}
                          onClick={() =>
                            void run(`in:${row.doc_guid}`, async () => {
                              const result = await exclusionsApi.include(row.database_id, row.doc_guid)
                              setToast({ text: `${row.stream_title ?? 'Документ'} ${row.doc_number ?? ''} возвращён. ${result.note}`, id: Date.now() })
                              setFound((current) => current?.map((item) => (keyOf(item) === key ? { ...item, excluded: false } : item)) ?? null)
                              // Строка гаснет на месте и только потом уходит из списка.
                              setLeaving(key)
                              await new Promise((resolve) => window.setTimeout(resolve, durationToken('--dur-leave', 200)))
                            })
                          }
                        >
                          {isBusy(`in:${row.doc_guid}`) ? 'Возвращаем…' : 'Вернуть'}
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>
      {toast && <Toast key={toast.id} text={toast.text} onDone={() => setToast(null)} />}
    </div>
  )
}

/**
 * Подтверждение действия поверх страницы, а не над ней: ничего не
 * сдвигает. Поднимается (--dur-enter), держится пять секунд, гаснет.
 */
function Toast({ text, onDone }: { text: string; onDone: () => void }) {
  const [leaving, setLeaving] = useState(false)
  useEffect(() => {
    const hide = window.setTimeout(() => setLeaving(true), 5000)
    const done = window.setTimeout(onDone, 5000 + durationToken('--dur-leave', 200))
    return () => {
      window.clearTimeout(hide)
      window.clearTimeout(done)
    }
  }, [])
  return (
    <div className={`toast${leaving ? ' toast--leave' : ''}`} role="status">
      <Icon name="check" size={16} className="toast__icon" />
      <span className="toast__text">{text}</span>
      <button type="button" className="btn btn--ghost btn--sm btn--icon" onClick={() => setLeaving(true)} aria-label="Закрыть">
        <Icon name="x" size={14} />
      </button>
    </div>
  )
}

// --- Статьи ДДС ----------------------------------------------------------------

function CashflowTab({ options }: { options: FinanceOptions }) {
  const [data, setData] = useState<{ rows: CashflowItemClassRow[]; window: { from: string | null; to: string | null } } | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [showUnused, setShowUnused] = useState(false)
  const load = async () => {
    try {
      setData(await financeApi.cashflowItems())
    } catch (caught) {
      setLoadError(caught instanceof ApiError ? caught.message : 'Не удалось загрузить статьи ДДС')
    }
  }
  const { isBusy, error, run, mark, flash } = useAction(load)
  // Выбранное значение стоит в поле сразу, до ответа сервера: поле не
  // откатывается на прежнее, пока идёт сохранение и перечитывание списка.
  const [draft, setDraft] = useState<Record<string, Partial<CashflowItemClassRow>>>({})
  useEffect(() => {
    void load()
  }, [])

  if (loadError) return <Notice tone="bad">{loadError}</Notice>
  if (!data) return <Loading rows={8} />

  const used = data.rows.filter((row) => row.operations > 0)
  const shown = showUnused ? data.rows : used
  const save = async (row: CashflowItemClassRow, patch: Partial<Pick<CashflowItemClassRow, 'cf_section' | 'flow' | 'pnl_line'>>) => {
    setDraft((current) => ({ ...current, [row.guid]: { ...current[row.guid], ...patch } }))
    await run(row.guid, () =>
      financeApi.setCashflowItem(row.guid, {
        cf_section: patch.cf_section ?? row.cf_section,
        flow: patch.flow ?? row.flow,
        pnl_line: patch.pnl_line !== undefined ? patch.pnl_line : row.pnl_line,
      }),
    )
    setDraft((current) => {
      const next = { ...current }
      delete next[row.guid]
      return next
    })
  }

  return (
    <div className="stack" style={{ gap: 18 }}>
      {error && <Notice tone="bad">{error}</Notice>}
      <Card
        flush
        title="Статьи движения денежных средств"
        subtitle={`${pluralize(used.length, 'статья в работе', 'статьи в работе', 'статей в работе')} из ${data.rows.length}${data.window.from && data.window.to ? ` · обороты за ${formatMonth(data.window.from)} — ${formatMonth(data.window.to)}` : ''}`}
        actions={
          <button type="button" className="btn btn--sm btn--ghost" onClick={() => setShowUnused((value) => !value)}>
            {showUnused ? 'Только статьи в работе' : 'Показать все статьи'}
          </button>
        }
      >
        {shown.length === 0 ? (
          <Empty title="Статей ДДС ещё нет" hint="Они придут справочником или с первым пакетом движения денег." />
        ) : (
          <TableWrap cards>
            <table className="data">
              <thead>
                <tr>
                  <th>Статья</th>
                  <th className="num">Поступления</th>
                  <th className="num">Выплаты</th>
                  <th>Раздел</th>
                  <th>Направление</th>
                  <th>Строка P&L</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {shown.map((base) => {
                  const row = { ...base, ...draft[base.guid] }
                  return (
                  <tr key={row.guid} className={flash(row.guid)}>
                    <td>
                      <div className="strong">{row.name}</div>
                      <div className="tiny muted">
                        {row.operations > 0 ? pluralize(row.operations, 'операция', 'операции', 'операций') : 'без операций'}
                        {row.source === 'manual' ? ` · вручную${row.updated_by ? `, ${row.updated_by}` : ''}` : ' · по имени'}
                      </div>
                    </td>
                    <td className="num mono">{money(row.inflow)}</td>
                    <td className="num mono">{money(row.outflow)}</td>
                    <td>
                      <select className="select select--sm" value={row.cf_section} disabled={isBusy(row.guid)} onChange={(event) => void save(row, { cf_section: event.target.value })} aria-label={`Раздел статьи «${row.name}»`}>
                        {options.cf_sections.map((value) => (
                          <option key={value} value={value}>{value}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select className="select select--sm" value={row.flow} disabled={isBusy(row.guid)} onChange={(event) => void save(row, { flow: event.target.value })} aria-label={`Направление статьи «${row.name}»`}>
                        {options.cf_flows.map((value) => (
                          <option key={value} value={value}>{value}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select className="select select--sm" value={row.pnl_line ?? ''} disabled={isBusy(row.guid)} onChange={(event) => void save(row, { pnl_line: event.target.value || null })} aria-label={`Строка P&L статьи «${row.name}»`}>
                        <option value="">Не относится к P&L</option>
                        {options.cashflow_pnl_lines.map((value) => (
                          <option key={value} value={value}>{value}</option>
                        ))}
                      </select>
                    </td>
                    <td className="num">
                      <div className="row row--tight row--end">
                      {mark(row.guid)}
                      {row.source === 'manual' && (
                        <button
                          type="button"
                          className="btn btn--sm btn--ghost"
                          disabled={isBusy(row.guid)}
                          title={`По имени: ${row.suggested.cf_section}, ${row.suggested.flow}, ${row.suggested.pnl_line ?? 'не в P&L'}`}
                          onClick={() => void run(row.guid, () => financeApi.resetCashflowItem(row.guid))}
                        >
                          Как по имени
                        </button>
                      )}
                      </div>
                    </td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>
      <p className="tiny muted">
        Строка P&L нужна для сверки: выплаты по статье сравниваются с расходами, проведёнными поступлением услуг.
        «Внутренние перемещения» — переводы между своими счетами и организациями.
      </p>
    </div>
  )
}

// --- Номенклатура услуг ---------------------------------------------------------

function ExpenseTab({ options }: { options: FinanceOptions }) {
  const [data, setData] = useState<{ rows: ExpenseItemClassRow[]; window: { from: string | null; to: string | null } } | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const load = async () => {
    try {
      setData(await financeApi.expenseItems())
    } catch (caught) {
      setLoadError(caught instanceof ApiError ? caught.message : 'Не удалось загрузить номенклатуру услуг')
    }
  }
  const { isBusy, error, run, mark, flash } = useAction(load)
  const [draft, setDraft] = useState<Record<string, string>>({})
  useEffect(() => {
    void load()
  }, [])

  if (loadError) return <Notice tone="bad">{loadError}</Notice>
  if (!data) return <Loading rows={8} />

  const saveLine = async (guid: string, line: string) => {
    setDraft((current) => ({ ...current, [guid]: line }))
    await run(guid, () => financeApi.setExpenseItem(guid, { pnl_line: line }))
    setDraft((current) => {
      const next = { ...current }
      delete next[guid]
      return next
    })
  }

  const unclassified = data.rows.filter((row) => row.pnl_line === 'Не размечено')

  return (
    <div className="stack" style={{ gap: 18 }}>
      {error && <Notice tone="bad">{error}</Notice>}
      {unclassified.length > 0 && (
        <Notice tone="plain">
          Без статьи — {pluralize(unclassified.length, 'услуга', 'услуги', 'услуг')}. В черновом P&L они входят в расходы строкой
          «Не размечено»: так EBITDA не завышается, но и не раскладывается.
        </Notice>
      )}
      <Card
        flush
        title="Номенклатура услуг в поступлениях → строка P&L"
        subtitle={`Расходы клиент проводит поступлением услуг, номенклатура служит статьёй${data.window.from && data.window.to ? ` · суммы без НДС за ${formatMonth(data.window.from)} — ${formatMonth(data.window.to)}` : ''}`}
      >
        {data.rows.length === 0 ? (
          <Empty title="Поступлений услуг ещё нет" hint="Строки появятся с первым пакетом поступлений, где есть номенклатура вида «Услуга»." />
        ) : (
          <TableWrap cards>
            <table className="data">
              <thead>
                <tr>
                  <th>Услуга</th>
                  <th className="num">За 12 мес.</th>
                  <th className="num">Всего</th>
                  <th>Строка P&L</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={row.guid} className={flash(row.guid)}>
                    <td>
                      <div className="strong">{row.name}</div>
                      <div className="tiny muted">
                        {pluralize(row.lines, 'строка', 'строки', 'строк')}
                        {row.source === 'manual' ? ` · вручную${row.updated_by ? `, ${row.updated_by}` : ''}` : ' · по имени'}
                      </div>
                    </td>
                    <td className="num mono">{money(row.amount_12m)}</td>
                    <td className="num mono muted">{money(row.amount_total)}</td>
                    <td>
                      <select
                        className="select select--sm"
                        value={draft[row.guid] ?? row.pnl_line}
                        disabled={isBusy(row.guid)}
                        onChange={(event) => void saveLine(row.guid, event.target.value)}
                        aria-label={`Строка P&L услуги «${row.name}»`}
                      >
                        {options.expense_lines.map((value) => (
                          <option key={value} value={value}>{value}</option>
                        ))}
                      </select>
                    </td>
                    <td className="num">
                      <div className="row row--tight row--end">
                        {mark(row.guid)}
                        {row.source === 'manual' && (
                          <button type="button" className="btn btn--sm btn--ghost" disabled={isBusy(row.guid)} title={`По имени: ${row.suggested}`} onClick={() => void run(row.guid, () => financeApi.resetExpenseItem(row.guid))}>
                            Как по имени
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>
      <p className="tiny muted">
        «Не расход периода» — услуга перепродана клиенту или вошла в себестоимость: в EBITDA не идёт, но в P&L видна отдельной суммой.
      </p>
    </div>
  )
}
